import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Check, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { STACK_ICONS, STACK_COLORS } from '@/lib/store';

export function StackCard({ stack, progress, showProgress = true }) {
    const navigate = useNavigate();
    
    const icon = STACK_ICONS.find(i => i.name === stack.icon) || STACK_ICONS[0];
    const color = STACK_COLORS.find(c => c.name === stack.color) || STACK_COLORS[0];
    
    const completedCount = progress?.completedToday || 0;
    const totalCount = progress?.totalItems || stack.items?.length || 0;
    const isComplete = completedCount >= totalCount && totalCount > 0;
    const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
    
    return (
        <div 
            className={cn(
                "relative bg-card rounded-lg border transition-all duration-200",
                isComplete 
                    ? "border-success/30 bg-success-light/30" 
                    : "border-border hover:border-primary/30 hover:shadow-card"
            )}
        >
            <div className="p-4">
                {/* Top row: Icon, Name, and Action */}
                <div className="flex items-center gap-3 mb-3">
                    {/* Icon */}
                    <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center text-xl shrink-0",
                        color.lightClass
                    )}>
                        {icon.emoji}
                    </div>
                    
                    {/* Name and status */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-foreground truncate">
                                {stack.name}
                            </h3>
                            {isComplete && (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-success">
                                    <Check className="w-3 h-3 text-success-foreground" />
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                            {isComplete ? 'Completed' : `${completedCount} of ${totalCount}`}
                        </p>
                    </div>
                    
                    {/* Action button - compact, right-aligned */}
                    {isComplete ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/run/${stack.id}`)}
                            className="h-8 px-3 text-muted-foreground hover:text-foreground"
                        >
                            Review
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                    ) : (
                        <Button
                            onClick={() => navigate(`/run/${stack.id}`)}
                            size="sm"
                            className={cn(
                                "h-9 gap-1.5 font-medium",
                                color.class,
                                "text-white hover:opacity-90"
                            )}
                        >
                            <Play className="w-3.5 h-3.5 fill-current" />
                            Run
                        </Button>
                    )}
                </div>
                
                {/* Progress bar */}
                {showProgress && (
                    <Progress 
                        value={progressPercent} 
                        className="h-1.5"
                        indicatorClassName={cn(
                            "transition-all duration-500",
                            isComplete ? "bg-success" : color.class
                        )}
                    />
                )}
            </div>
        </div>
    );
}
